/* eslint-disable @typescript-eslint/no-explicit-any */
// react-i18next + TS 4.9.5 workaround — see src/i18n/index.ts.
import React, { useMemo, useState } from 'react';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import { isAuthV2Enabled } from '../../lib/auth/authV2Flag';
import { ProfileType } from '../../types/enums';
import { AvailableProfileDto } from '../../services/api';
import styles from './MultiProfileBanner.module.css';

const useTranslation = useTranslationUntyped as any;

const SEEN_KEY = 'authV2.switcher.seen';

/**
 * Auth v2 first-time multi-profile discovery banner (AB#1440).
 *
 * Shown once to users who have >1 surface-able profile (excluding Customer per
 * PO-D-002) and have not yet dismissed it. Points them at the account menu /
 * switcher so they know how to change profile without logging out.
 *
 * Hidden when:
 *   - Auth v2 flag is off, OR
 *   - `localStorage[SEEN_KEY]` is truthy (previously dismissed), OR
 *   - fewer than 2 surface-able profiles (no switcher → no banner), OR
 *   - no active profile (chooser-pending token — banner doesn't apply).
 */
const MultiProfileBanner: React.FC = () => {
    const { t } = useTranslation('auth');
    const [dismissed, setDismissed] = useState(() => Boolean(localStorage.getItem(SEEN_KEY)));

    const { active, profiles } = useMemo(readAvailableProfilesFromToken, []);

    if (!isAuthV2Enabled()) return null;
    if (dismissed) return null;

    const surfaceable = profiles.filter(p => p.type !== ProfileType.Customer);
    if (surfaceable.length < 2) return null;
    if (!active) return null;

    const handleDismiss = () => {
        try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* noop */ }
        setDismissed(true);
    };

    const activeRoleLabel = (() => {
        switch (active.type) {
            case ProfileType.SalonOwner: return t('roles.salonOwner');
            case ProfileType.SalonStaff: return t('roles.salonStaff');
            case ProfileType.SuperAdmin: return t('roles.superAdmin');
            case ProfileType.Customer: return t('roles.customer');
            default: return String(active.type);
        }
    })();

    return (
        <div className={styles.banner} role="status">
            <span className={styles.icon} aria-hidden="true">👋</span>
            <span
                className={styles.text}
                // Body contains bold span for role+business; set via i18n with variables.
                // We render plain text + let CSS style .businessLabel; keep markup simple.
            >
                {t('banner.welcome', {
                    role: activeRoleLabel,
                    business: active.businessName ?? '',
                })}
            </span>
            <button
                type="button"
                className={styles.close}
                onClick={handleDismiss}
                aria-label={t('banner.dismiss')}
            >
                ×
            </button>
        </div>
    );
};

function readAvailableProfilesFromToken(): { active: AvailableProfileDto | null; profiles: AvailableProfileDto[] } {
    try {
        const token = localStorage.getItem('accessToken');
        if (!token) return { active: null, profiles: [] };
        const payload = token.split('.')[1];
        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));

        const activeId = parseInt(decoded['userProfileId'] ?? '', 10);
        const raw = decoded['availableProfiles'];
        if (!raw) return { active: null, profiles: [] };
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!Array.isArray(parsed)) return { active: null, profiles: [] };

        const profiles: AvailableProfileDto[] = parsed.map((p: any) => ({
            id: typeof p.id === 'number' ? p.id : parseInt(p.id, 10),
            type: mapProfileType(p.type),
            businessId: p.businessId ?? null,
            businessName: p.businessName ?? null,
        }));

        const active = profiles.find(p => p.id === activeId) ?? null;
        return { active, profiles };
    } catch {
        return { active: null, profiles: [] };
    }
}

function mapProfileType(raw: unknown): ProfileType {
    if (typeof raw === 'number') return raw as ProfileType;
    if (typeof raw === 'string') {
        switch (raw) {
            case 'Customer': return ProfileType.Customer;
            case 'SalonOwner': return ProfileType.SalonOwner;
            case 'SalonStaff': return ProfileType.SalonStaff;
            case 'SuperAdmin': return ProfileType.SuperAdmin;
            default: return ProfileType.Customer;
        }
    }
    return ProfileType.Customer;
}

export default MultiProfileBanner;
